import { useContext } from "react";

import type { TAuthenticationMethod } from "@mne-authentication/api";
import { SessionContext, Unauthenticated } from "@mne-authentication/session2";

import Typography from "@mui/material/Typography";

import { defineMessages, useIntl } from "@mnemonic/intl";
import { LogClient as Logger } from "@mnemonic/logging";
import { PageLayout, RemoveSplash } from "@mnemonic/mui";

import UserAuthorizationBase from "../../Components/UserAuthorizationBase";
import { commonMessages } from "../../commonMessages";
import RedirectToLoginPage from "../../utils/RedirectToLoginPage";
import { AuthorizationWithEnrollment } from "./AuthorizationWithEnrollment";
import { AuthorizationWithLDAP } from "./AuthorizationWithLDAP";
import { AuthorizationWithOpenID } from "./AuthorizationWithOpenID";
import { AuthorizationWithSMSToken } from "./AuthorizationWithSMSToken";
import { AuthorizationWithTOTP } from "./AuthorizationWithTOTP";
import { AuthorizationWithUsernameAndPassword } from "./AuthorizationWithUsernameAndPassword";
import { AuthorizationWithWebAuthn } from "./AuthorizationWithWebAuthn";
import ErrorCard from "./ErrorCardQueryParameters";
import { UnsupportedMethod } from "./UnsupportedMethod";
import { getOperationTitle, getUserAuthSearch } from "./utils/OperationUtils";

const messages = defineMessages({
  userAuthorization: { id: "user.authorization", defaultMessage: "User Authorization" },
});

export const AuthorizationPage = () => {
  const { session } = useContext(SessionContext);
  const { context, next: nextURI, operation } = getUserAuthSearch(location.href);
  const intl = useIntl();
  const showErrorCard = !(Boolean(operation) && Boolean(nextURI));
  const allowAuthorization = Boolean(operation) && Boolean(nextURI);
  const authenticationMethod = session.info.authenticationMethod;

  const sharedProps = {
    context: Object.assign({}, context) as unknown as { [key: string]: string },
    nextURI,
    operation,
  };

  const getComponentForAuthMethod = (method: TAuthenticationMethod) => {
    switch (method) {
      case "LDAP":
        return <AuthorizationWithLDAP {...sharedProps} />;
      case "SMS":
        return <AuthorizationWithSMSToken {...sharedProps} />;
      case "ENROLLMENT":
        return <AuthorizationWithEnrollment {...sharedProps} />;
      case "PASSWORD":
        return <AuthorizationWithUsernameAndPassword {...sharedProps} />;
      case "TOTP":
        return <AuthorizationWithTOTP {...sharedProps} />;
      case "OPENID":
        return <AuthorizationWithOpenID {...sharedProps} />;
      case "SIGNATURE":
        return <AuthorizationWithWebAuthn {...sharedProps} />;
      // These shouldn't be able to authorize actions
      case "OTP":
      case "APIKEY":
        Logger.getInstance().logInfo(
          `Authorization page entered with unsupported session.authenticationMethod: ${method}`,
        );
        return null;
      // Exhaustive check to ensure that all methods are accounted for
      default: {
        const _exhaustiveCheck: never = method;
        Logger.getInstance().logWarn(
          `Authorization page entered with unexpected session.authenticationMethod: ${_exhaustiveCheck}`,
        );
        return null;
      }
    }
  };
  const componentForAuthMethod = getComponentForAuthMethod(authenticationMethod);
  const title =
    (!componentForAuthMethod && intl.formatMessage(commonMessages.UnsupportedAuthorizationMethod_title)) ||
    getOperationTitle(operation, intl);

  return (
    <PageLayout
      PageHeaderProps={{
        content: <Typography variant="h6">{intl.formatMessage(messages.userAuthorization)}</Typography>,
      }}
    >
      {session && (
        <>
          <RemoveSplash />
          {showErrorCard && <ErrorCard {...{ intl }} />}
          {allowAuthorization && (
            <UserAuthorizationBase {...{ title, context }}>
              {getComponentForAuthMethod(authenticationMethod)}
              {!componentForAuthMethod && <UnsupportedMethod method={authenticationMethod} nextURI={nextURI} />}
            </UserAuthorizationBase>
          )}
        </>
      )}
      <Unauthenticated>
        <RedirectToLoginPage />
      </Unauthenticated>
    </PageLayout>
  );
};
